import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimContractObjectData } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimImportContractObjectsGql } from '../BimContractStructureRequests';

interface IUseBimUploadContractObjects {
    contractId: number;
    isDataExists?: boolean;
    bimContractObjectsData: BimContractObjectData[];
}

export const useBimUploadContractObjects = (props: IUseBimUploadContractObjects) => {
    const { contractId, isDataExists, bimContractObjectsData } = props;
    const dispatch = useDispatch();

    const [files, setFiles] = useState<File[]>([]);

    const base64Files = useReadFilesAsBase64(files);

    const requestId = useMemo(
        () => (isDataExists ? 'bimReImportContractObjects' : 'bimImportContractObjects'),
        [isDataExists],
    );

    const [resetBimImportContractObjects] = useResetStoreData({ requestId });

    const resetUploadData = useCallback(() => {
        setFiles([]);
        resetBimImportContractObjects(undefined);
    }, [resetBimImportContractObjects]);

    const [bimImportContractObjectsData, bimImportContractObjectsStatus, , bimImportContractObjectsRequest] =
        useRequest({
            requestId,
            query: bimImportContractObjectsGql,
            withEffect: false,
        });

    const onSetFileDownloadObjects = useCallback(
        (filesToUpload: File[]) => {
            if (filesToUpload.length && contractId) {
                setFiles(filesToUpload);
            }
        },
        [contractId],
    );

    const doUpload = useCallback(
        () =>
            base64Files &&
            dispatch(
                bimImportContractObjectsRequest({
                    contractId,
                    fileContent: base64Files[0],
                }),
            ),
        [base64Files, bimImportContractObjectsRequest, contractId, dispatch],
    );

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: doUpload,
        });

    const onCloseConfirmationDialog = useCallback(() => {
        setFiles([]);
        handleCloseConfirmationDialog();
    }, [handleCloseConfirmationDialog]);

    useEffect(() => {
        if (
            files?.length &&
            base64Files?.length &&
            !bimImportContractObjectsData?.bimImportContractObjects?.data?.id &&
            bimImportContractObjectsStatus !== RequestStatus.FETCHING
        ) {
            if (!isDataExists) {
                doUpload();
            } else {
                handleOpenConfirmationDialog();
            }
        }
    }, [
        base64Files,
        bimImportContractObjectsData?.bimImportContractObjects?.data?.id,
        bimImportContractObjectsRequest,
        bimImportContractObjectsStatus,
        contractId,
        dispatch,
        doUpload,
        files?.length,
        handleOpenConfirmationDialog,
        isDataExists,
    ]);

    useEffect(() => {
        if (
            bimImportContractObjectsStatus !== RequestStatus.FETCHING &&
            (bimImportContractObjectsStatus === RequestStatus.ERROR ||
                !bimImportContractObjectsData?.bimImportContractObjects?.result)
        ) {
            setFiles([]);
        }
    }, [bimImportContractObjectsData?.bimImportContractObjects?.result, bimImportContractObjectsStatus]);

    useEffect(() => {
        resetBimImportContractObjects(undefined);
        setFiles([]);
    }, [resetBimImportContractObjects, bimContractObjectsData]);

    return {
        bimImportContractObjectsData: bimImportContractObjectsData?.bimImportContractObjects,
        bimImportContractObjectsStatus,
        bimImportContractObjectsRequest,
        onSetFileDownloadObjects,
        isOpenImportContractObjectsConfirmationDialog: isOpenConfirmationDialog,
        onCloseImportContractObjectsConfirmationDialog: onCloseConfirmationDialog,
        onActionImportContractObjectsConfirmationDialog: handleActionQuery,
        resetUploadData,
    };
};
